import { logEventFirebase } from 'utils/config/firebase';
import { copyMessage } from './messages';
import { onCopyValue } from './copy';

interface ValuesType {
    value?: string;
    isSelection?: boolean;
    noMessage?: boolean;
}

export const copy = ({ value = '', isSelection, noMessage }: ValuesType) => {
    logEventFirebase('click_copy_to_clipboard');
    let data = {};
    if (!noMessage) {
        data = { onCopy: copyMessage, isSelection };
    } else {
        data = { isSelection };
    }
    onCopyValue(value, data);
};

export const minifyHtml = (html: string) => {
    return html
        ?.replace(/\n+/g, '\n')
        .replace(/\s{2,}/g, '')
        .replace(/\s</g, '<')
        .replace(/>\s/g, '>');
};

export const minifyHtmlOutput = (html: string) => {
    return html?.replace(/\n+/g, '\n');
};

export const removeEmptyBrTags = (html: string) => {
    // Regular expression that match HTML tags containing only <br>
    const regex = /<(\w+)[^>]*><br><\/\1>/g;

    return html?.replace(regex, '');
};

export const removeDashesFromLi = (html: string): string => html?.replace(/<li>- /g, '<li>');

/**
 * Formats Quill HTML content for optimal Google Docs compatibility while preserving all formatting.
 * Converts Quill-specific classes to inline styles that Google Docs understands.
 *
 * @param html - Raw HTML content from Quill editor
 * @returns Formatted HTML optimized for pasting into Google Docs with preserved formatting
 *
 * @example
 * const quillHtml = quillRef.current.root.innerHTML;
 * const googleDocsHtml = formatHtmlForGoogleDocs(quillHtml);
 * // Now copy googleDocsHtml to clipboard
 */
export const formatHtmlForGoogleDocs = (html: string): string => {
    if (!html) {
        return '';
    }

    // Create a temporary DOM element to manipulate the HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Convert Quill alignment classes to inline styles
    temp.querySelectorAll('[class*="ql-align"]').forEach((el) => {
        const className = el.getAttribute('class') || '';
        if (className.includes('ql-align-center')) {
            (el as HTMLElement).style.textAlign = 'center';
        } else if (className.includes('ql-align-right')) {
            (el as HTMLElement).style.textAlign = 'right';
        } else if (className.includes('ql-align-justify')) {
            (el as HTMLElement).style.textAlign = 'justify';
        }
        // Remove the ql-align class after converting
        el.setAttribute('class', className.replace(/ql-align-\w+/g, '').trim());
    });

    // Convert Quill indent classes to inline styles
    temp.querySelectorAll('[class*="ql-indent"]').forEach((el) => {
        const className = el.getAttribute('class') || '';
        const indentMatch = className.match(/ql-indent-(\d+)/);
        if (indentMatch) {
            const indentLevel = parseInt(indentMatch[1], 10);
            (el as HTMLElement).style.paddingLeft = `${indentLevel * 30}px`;
        }
        // Remove the ql-indent class after converting
        el.setAttribute('class', className.replace(/ql-indent-\d+/g, '').trim());
    });

    // Handle Quill font size classes
    temp.querySelectorAll('[class*="ql-size"]').forEach((el) => {
        const className = el.getAttribute('class') || '';
        if (className.includes('ql-size-small')) {
            (el as HTMLElement).style.fontSize = '10px';
        } else if (className.includes('ql-size-large')) {
            (el as HTMLElement).style.fontSize = '18px';
        } else if (className.includes('ql-size-huge')) {
            (el as HTMLElement).style.fontSize = '32px';
        }
        el.setAttribute('class', className.replace(/ql-size-\w+/g, '').trim());
    });

    // Remove any remaining Quill-specific classes
    temp.querySelectorAll('[class]').forEach((el) => {
        const className = el.getAttribute('class') || '';
        const cleanedClass = className.replace(/ql-\S+/g, '').trim();
        if (cleanedClass) {
            el.setAttribute('class', cleanedClass);
        } else {
            el.removeAttribute('class');
        }
    });

    // Clean up tables for better Google Docs compatibility
    temp.querySelectorAll('table').forEach((table) => {
        // Add basic table styling that Google Docs understands
        table.setAttribute('border', '1');
        table.setAttribute('cellpadding', '4');
        table.setAttribute('cellspacing', '0');
        (table as HTMLElement).style.borderCollapse = 'collapse';
        (table as HTMLElement).style.width = '100%';

        // Keep cell styles for Google Docs
        table.querySelectorAll('td, th').forEach((cell) => {
            // Keep background colors, text alignment, etc.
            const style = (cell as HTMLElement).style;
            if (!style.padding) {
                style.padding = '8px';
            }
            if (!style.border) {
                style.border = '1px solid #000';
            }
        });
    });

    // Preserve bold, italic, underline in semantic tags
    // Quill already uses <strong>, <em>, <u>, <s> so these are preserved automatically

    // Ensure links have proper attributes
    temp.querySelectorAll('a').forEach((link) => {
        if (link.getAttribute('href')) {
            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        }
    });

    // Remove data-* attributes that Quill might add (these don't affect formatting)
    temp.querySelectorAll('[data-row], [data-col], [data-table-id]').forEach((el) => {
        Array.from(el.attributes).forEach((attr) => {
            if (attr.name.startsWith('data-')) {
                el.removeAttribute(attr.name);
            }
        });
    });

    // Remove empty paragraphs (but keep <br> for line breaks)
    temp.querySelectorAll('p:empty, div:empty').forEach((el) => {
        // Only remove if it doesn't have a <br> child
        if (!el.querySelector('br')) {
            el.remove();
        }
    });

    let cleanedHtml = temp.innerHTML;

    // Light whitespace normalization (don't remove too much)
    cleanedHtml = cleanedHtml
        .replace(/>\s+</g, '><') // Remove whitespace between tags
        .trim();

    return cleanedHtml;
};
